import { type SignedUrlResponse } from '@xpeng/xdragon-ass-components-eu/es/api/baseService/file';
import { SITE_CODE, getSiteCode } from '@xpeng/xdragon-ass-components-eu/es/utils/biz';
import {
  batchDownload,
  generateSignedUrl,
  getUploadUrl,
  queryFileInfoApi,
  saveFileInfo
} from './service';
import type { TQueryFileInfoParams } from '@/api/queryFileInfo';
import { uploadByXHR, type UploadFileByXHROptions } from '../xhr';

type UploadFileOptions = Partial<UploadFileByXHROptions> & {
  withFileId?: boolean;
  unzip?: boolean;
};

type UploadResult = { uploadUrl: string; downloadUrl: string; fileId?: string };
type QueryFileInfoResult = {
  name: string;
  url: string;
  uid: string;
  size: number;
  fileId: string;
}[];

async function generateSignedUrlEU(file: File) {
  const resp = await generateSignedUrl({
    fileName: file.name,
    fileSize: file.size
  });
  if (resp.code === 200) {
    return resp.data;
  } else {
    throw new Error(resp.msg);
  }
}

// 国内还是走旧流程
async function generateSignedUrlCN(file: File, unzip: boolean = false) {
  const resp = await getUploadUrl({ fileName: file.name, unzip });
  if (resp.code === 200) {
    return resp.data;
  } else {
    throw new Error(resp.msg);
  }
}

export async function uploadEpc(
  file: File,
  options: UploadFileOptions = {},
  queryMaskOptions?: any
) {
  const siteCode = getSiteCode();
  const { onProgress = () => {}, withFileId = false } = options;
  const data = await (siteCode === SITE_CODE.EU
    ? generateSignedUrlEU(file)
    : generateSignedUrlCN(file, options.unzip));

  const { uploadUrl, downloadUrl } = data;
  const result: UploadResult = { uploadUrl, downloadUrl };

  if (file.type === 'image/svg+xml') {
    await uploadByXHR(uploadUrl, file, {
      onProgress,
      contentType: 'image/svg+xml'
    });
  } else {
    await uploadByXHR(uploadUrl, file, {
      onProgress,
      contentType: 'application/octet-stream'
    });
  }

  if (withFileId) {
    if (siteCode === SITE_CODE.EU) {
      result.fileId = (data as SignedUrlResponse).fileId!;
    } else {
      const resp = await saveFileInfo({
        url: downloadUrl,
        ...queryMaskOptions
      });
      if (resp.code === 200) {
        result.fileId = resp.data?.splicingFileIds as string;
      } else {
        throw new Error(resp.msg);
      }
    }
  }

  return result;
}

// 指量获取文件信息
export async function queryFileInfoEpc(
  _fileIds: string[] = [],
  options: Omit<TQueryFileInfoParams, 'splicingFileIds'> = {}
) {
  // 去除空文件
  const fileIds = _fileIds.filter(file => file);
  if (!fileIds.length) return [];

  const siteCode = getSiteCode();
  // 国际逻辑
  if (siteCode === SITE_CODE.EU) {
    const resp = await batchDownload(fileIds.map(fileId => ({ fileId })));
    if (resp.code === 200) {
      const files = resp.data || [];
      return files.map(item => ({
        name: item.fileName,
        url: item.downloadUrl,
        uid: item.fileId,
        size: item.fileSize / 1024, // 这里转为KB，以跟国内保持一致
        fileId: item.fileId
      }));
    } else {
      throw new Error(resp.msg);
    }
    // 国内逻辑
  } else {
    const resp = await queryFileInfoApi({
      ...options,
      splicingFileIds: fileIds.join(',')
    });
    if (resp.code === 200) {
      const files = resp.data || [];
      return files.map((item: any) => ({
        name: item.fileName + '.' + item.fileType,
        url: item.filePath,
        uid: item.id,
        size: item.fileSize,
        fileId: item.fileId
      })) as QueryFileInfoResult;
    } else {
      throw new Error(resp.msg);
    }
  }
}
